<template>
  <v-text-field
    ref="textField"
    v-model="searchQuery"
    variant="outlined"
    density="compact"
    hide-details
    color="brandPrimary"
    base-color="textDarkest"
    :placeholder="selected?.length ? '' : placeholder"
    :disabled="disabled"
    class="full-width treeselect-search-inline"
    @keydown.delete="$emit('deleteKeyDown')"
    @focus="$emit('focus')"
  >
    <template #prepend-inner>
      <v-icon
        icon="$magnify"
        :size="20"
        color="iconDark"
        class="search-icon shrink-0"
      />
      <div
        v-for="option in selected.slice(0, 3)"
        :key="`pill${option[idKey]}`"
        class="inline-pill"
      >
        <slot
          name="chip-prepend"
          :option="option"
        />
        <span class="chip-label">
          {{ typeof labelKey === 'function' ? labelKey(option) : option[labelKey] }}
        </span>
        <v-icon
          icon="$close"
          :size="16"
          color="iconDefault"
          class="inline-pill-close"
          @click="$emit('removeOption', option)"
        />
      </div>
      <span
        v-if="selected.length > 3"
        class="and-more"
      >and {{ selected.length - 3 }} more</span>
    </template>
  </v-text-field>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  selected: {
    type: Array,
    default: () => ([]),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isSelect: {
    type: Boolean,
    default: false,
  },
  labelKey: {
    type: [String, Function],
    default: 'label',
  },
  idKey: {
    type: String,
    default: 'id',
  },
});

const emits = defineEmits([
  'update:modelValue', 'deleteKeyDown', 'removeOption', 'focus',
]);

const searchQuery = computed({
  get: () => props.modelValue,
  set: (value) => emits('update:modelValue', value),
});

const textField = ref(null);

onMounted(() => {
  if (!props.isSelect) textField.value.focus();
});
</script>

<style lang="less" scoped>
.full-width {
  width: 100%;
}

.and-more {
  font-size: 12px;
  font-weight: 600;
  padding: 1px 0;
  margin: 2px 0;
  cursor: default;
  color: fade(@grey-900, 30%);
}

.inline-pill {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  max-width: 215px;
  padding: 0 4px 0 8px;
  border: 1px solid @stroke-default;
  border-radius: 36px;
  background: @bg-default;

  .chip-label {
    font-size: 13px;
    font-weight: 500;
    color: @text-dark;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .inline-pill-close {
    cursor: pointer;
  }
}

.treeselect-search-inline {
  :deep(.v-input__control) {
    min-height: 40px;
  }

  :deep(.v-field) {
    --v-field-border-opacity: 1;

    display: flex;
    flex-wrap: wrap;
    min-height: 40px;
    min-width: 215px;
    padding: 0 12px;
    align-items: center;
    border-radius: 8px;
    background-color: @bg-default;
    color: @stroke-default !important;
    gap: 8px;

    &.v-field--focused {
      .v-field__outline__start,
      .v-field__outline__end {
        border-color: @text-brand !important;
        border-width: 2px !important;
      }

      .v-field__outline__start {
        border-right-width: 0 !important;
      }

      .v-field__outline__end {
        border-left-width: 0 !important;
      }
    }
  }

  :deep(.v-field__field) {
    align-items: center;
  }

  :deep(.v-field__outline) {
    .v-field__outline__start,
    .v-field__outline__end {
      border: 1px solid fade(@grey-900, 50%) !important;
    }

    .v-field__outline__start {
      border-right-width: 0 !important;
    }

    .v-field__outline__end {
      border-left-width: 0 !important;
    }
  }

  :deep(.v-field__prepend-inner) {
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    align-self: center;
  }
}
</style>
